import { RootState, ThunkDispatch } from "core-integration/src/store";
import { fetchEventLive } from "core-integration/src/store/elements/thunks";
import {
  getEntry,
  getEntryEventPicks,
  getEntryEventPoints,
} from "core-integration/src/store/entries/reducers";
import {
  fetchEntryEventPicks,
  fetchEntryPhaseStandings,
  fetchEntrySummary,
} from "core-integration/src/store/entries/thunks";
import {
  getCurrentEvent,
  getEventsById,
  getNextEvent,
} from "core-integration/src/store/events/reducers";
import { fetchFixtures } from "core-integration/src/store/fixtures/thunks";
import { getPhases } from "core-integration/src/store/phases/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { IPlayer } from "core-integration/src/store/player/types";
import { hideVisually } from "polished";
import React, { useCallback } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import styled, { css } from "styled-components";
import i18n from "../i18n";
import BaseReload from "../img/icons/reload.svg?react";
import { ChipName, getChipName } from "../utils/chips";
import { isRTL } from "../utils/locale";
import { integerDivide } from "../utils/points";
import ButtonLink from "./ButtonLink";
import { ControlArrowEnd, ControlArrowStart } from "./icons/Arrows";
import { TopScoreLink } from "./Link";
import {
  EventPager,
  PagerButton,
  PagerButtonNext,
  PagerHeading,
} from "./Pager";
import { BoxWrapStyles } from "./Utils";

const PointsScoreboard = styled.div`
  gap: ${(props) => props.theme.space[2]};
  align-items: center;
  justify-content: center;
  display: flex;
  padding-top: ${({ theme }) => theme.space[3]};
  border-top: ${({ theme }) => theme.borders[2]};
  color: ${(props) => props.theme.colors.white};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    gap: 9%;
  }
`;

const StyledSection = styled.section`
  margin-bottom: ${(props) => props.theme.space[3]};
  padding: ${(props) => props.theme.space[2]} ${(props) => props.theme.space[1]};
  border-radius: ${(props) => props.theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    padding: ${(props) => props.theme.space[3]};
    ${BoxWrapStyles}
  }
`;

const StyledScoreboardBox = styled.div`
  position: relative;
  flex: 1;
  max-width: 185px;
  padding: 0.8rem;
  border-radius: ${(props) => props.theme.radii[1]};
  background-image: linear-gradient(
    ${(props) => props.theme.colors.red} 10%,
    ${(props) => props.theme.colors.pink}
  );
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    padding: ${(props) => props.theme.space[2]};
  }
`;

const PrimaryHeading = styled.h4`
  margin: 0;
  font-family: ${(props) => props.theme.fonts.body};
  font-size: ${(props) => props.theme.fontSizes[1]};
  font-weight: 500;
`;

const PrimaryValue = styled.div`
  margin-bottom: 0.31rem;
  padding: 0.2rem 0;
  font-family: ${(props) => props.theme.fonts.body};
  font-size: 4.3rem;
  font-weight: 500;
`;

const PrimaryPanel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.space[1]};
`;

const ReloadButton = styled.button`
  position: absolute;
  inset-inline-start: 8px;
  top: 35%;
  padding: ${({ theme }) => theme.space[1]};
  border: 0;
  border-radius: ${({ theme }) => theme.radii[1]};
  background-color: ${({ theme }) => theme.colors.primary};
  line-height: 1;
  cursor: pointer;
`;

const Reload = styled(BaseReload)`
  fill: ${({ theme }) => theme.colors.white};
  ${
    isRTL(i18n.language)
      ? css`
          transform: scaleX(-1);
        `
      : css`
          transform: scaleX(1);
        `
  }
`;

const ReloadButtonText = styled.span`
  ${hideVisually()}
`;

const ChipStatus = styled.div`
  padding: ${({ theme }) => theme.radii[1]} ${({ theme }) => theme.radii[2]};
  border-radius: ${({ theme }) => theme.radii[1]};
  background-image: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.blue}
  );
  color: white;
  font-size: 1.2rem;
  font-family: ${({ theme }) => theme.fonts.body};
  text-align: center;
`;

const SecondaryItemInner = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: center;
  font-family: ${(props) => props.theme.fonts.body};
  font-size: ${(props) => props.theme.fontSizes[2]};
`;

const SecondaryHeading = styled.h4`
  flex: 1 0 auto;
  margin: 0;
  font-weight: 500;
  text-align: center;
  font-size: ${(props) => props.theme.fontSizes[1]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.red};
  margin-bottom: ${({ theme }) => theme.space[1]};
  padding-bottom: 0.6rem;
  font-family: ${(props) => props.theme.fonts.body};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    text-align: start;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    font-size: ${(props) => props.theme.fontSizes[2]};
  }
`;

const SecondaryValue = styled.div`
  flex: 1;
  text-align: center;
  font-weight: 500;
  font-size: 2.6rem;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    text-align: end;
  }
`;

// ===== PAGER COMPONENTS =====
const StyledEventPager = styled(EventPager)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.space[1]};
  padding: ${({ theme }) => theme.space[1]};
`;

const StylesPagerHeading = styled(PagerHeading)`
  margin: 0;
`;

const StyledPagerButton = styled(PagerButton)`
  flex-basis: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    flex-basis: 30%;
  }
`;

const StyledPagerButtonNext = styled(PagerButtonNext)`
  display: flex;
  justify-content: flex-end;
  margin-left: 0;
  flex-basis: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    flex-basis: 30%;
  }
`;

const NavigationText = styled.span`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    display: inline;
    margin: 0 ${({ theme }) => theme.space[1]};
  }
`;

const NavigationArrow = styled.span`
  svg {
    width: ${({ theme }) => theme.space[2]};
    height: ${({ theme }) => theme.space[2]};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    display: none;
  }
`;

type Props = WithTranslation;

interface ISecondaryItem {
  heading: string;
  value: React.ReactNode;
}

const SecondaryItem = ({ heading, value }: ISecondaryItem) => (
  <StyledScoreboardBox>
    <SecondaryItemInner>
      <SecondaryHeading>{heading}</SecondaryHeading>
      <SecondaryValue>{value}</SecondaryValue>
    </SecondaryItemInner>
  </StyledScoreboardBox>
);

function EntryEventScoreboard({ t }: Props): React.ReactNode {
  const dispatch = useDispatch<ThunkDispatch>();
  const params = useParams();

  const entryId = Number(params.entryId);
  const eventId = Number(params.eventId);

  const entry = useSelector((state: RootState) => getEntry(state, entryId));
  const phases = useSelector(getPhases);
  const event = useSelector(
    (state: RootState) => getEventsById(state)[eventId],
  );
  const nextEvent = useSelector(getNextEvent);
  const now = useSelector((state: RootState) => getCurrentEvent(state));
  const entryData = useSelector((state: RootState) =>
    getEntryEventPicks(state, entryId, eventId),
  );
  const player = useSelector(getPlayerData) as IPlayer;
  const eventPoints = useSelector((state: RootState) =>
    getEntryEventPoints(state, entryId, eventId),
  );

  const mine = Boolean(player && player.entry && player.entry === entryId);

  const activePhase = phases.find(
    (p) =>
      p.id > 1 &&
      p.start_event <= Number(eventId) &&
      p.stop_event >= Number(eventId),
  );

  const chipName = entryData?.active_chip
    ? getChipName(entryData.active_chip as ChipName, t)
    : "";

  const fetchDataAndFixtures = () => {
    fetchData();
    dispatch(fetchFixtures(Number(eventId)));
  };

  const fetchData = useCallback(() => {
    dispatch(fetchEntrySummary(Number(entryId)));
    dispatch(fetchEntryEventPicks(Number(entryId), Number(eventId)));
    dispatch(fetchEventLive(Number(eventId)));
    if (eventId && entryId) {
      dispatch(fetchEntryPhaseStandings(Number(entryId), activePhase!.id));
    }
  }, [activePhase, dispatch, entryId, eventId]);

  return (
    <StyledSection>
      {/* ROUND PAGER */}
      <StyledEventPager>
        <StyledPagerButton>
          {event.id > entry.started_event && (
            <ButtonLink
              to={`/entry/${entry.id}/event/${event.id - 1}`}
              variant="secondary"
              minwidth="true"
            >
              <NavigationArrow>
                <ControlArrowStart />
              </NavigationArrow>
              <NavigationText>
                {t("entryEvent.prev", "Previous")}
              </NavigationText>
            </ButtonLink>
          )}
        </StyledPagerButton>

        <StylesPagerHeading>{event.name}</StylesPagerHeading>

        <StyledPagerButtonNext>
          {now && event.id < now.id ? (
            <ButtonLink
              to={`/entry/${entry.id}/event/${event.id + 1}`}
              variant="secondary"
              minwidth="true"
            >
              <NavigationText>{t("entryEvent.next", "Next")}</NavigationText>
              <NavigationArrow>
                <ControlArrowEnd />
              </NavigationArrow>
            </ButtonLink>
          ) : mine && now && event.id === now.id && nextEvent ? (
            <ButtonLink to={`/my-team`} variant="secondary" minwidth="true">
              <NavigationText>{t("entryEvent.next", "Next")}</NavigationText>
              <NavigationArrow>
                <ControlArrowEnd />
              </NavigationArrow>
            </ButtonLink>
          ) : null}
        </StyledPagerButtonNext>
      </StyledEventPager>

      {/* SCOREBOARD */}
      <PointsScoreboard>
        <SecondaryItem
          heading={t("entryEvent.average", "Average GD Pts")}
          value={integerDivide(event.average_entry_score, 10) || "-"}
        />

        <StyledScoreboardBox>
          <PrimaryHeading>
            {event.finished
              ? t("entryEvent.finalPoints", "Final GD Points")
              : t("entryEvent.latestPoints", "Latest GD Points")}
          </PrimaryHeading>
          <PrimaryValue>{integerDivide(eventPoints, 10)}</PrimaryValue>
          <PrimaryPanel>
            {!event.finished && (
              <ReloadButton onClick={fetchDataAndFixtures}>
                <ReloadButtonText>
                  {t("entryEvent.reload", "Reload Points")}
                </ReloadButtonText>
                <Reload />
              </ReloadButton>
            )}
            {chipName && (
              <ChipStatus>
                {t("entryEvent.chip.played", "{{ chipName }} played", {
                  chipName: chipName,
                })}
              </ChipStatus>
            )}
          </PrimaryPanel>
        </StyledScoreboardBox>

        <SecondaryItem
          heading={t("entryEvent.highest", "Top GD Points")}
          value={
            event.highest_scoring_entry ? (
              <TopScoreLink
                to={`/entry/${event.highest_scoring_entry}/event/${event.id}`}
              >
                {integerDivide(event.highest_score as number, 10) || "???"}
              </TopScoreLink>
            ) : (
              "-"
            )
          }
        />
      </PointsScoreboard>
    </StyledSection>
  );
}

export default withTranslation()(EntryEventScoreboard);
